import React, { useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { pathOr, prop, propOr, reverse, sortBy } from 'ramda'
import { DATE_FORMATS, formatDate } from 'utils/date'
import { TEACHER_DEBIT_ITEM_TYPES } from 'utils/teacher'
import { selectCurrentTeacherDebitItems } from 'modules/teachers/ducks/selectors'
import styled from 'styled-components'
import DeleteSalaryModal from 'modules/teacherDetails/components/DeleteSalaryModal'
import EditSalaryModal from 'modules/teacherDetails/components/EditSalaryModal'
import { isNilOrEmpty, isNotNilOrEmpty } from 'utils/ramda'
import EditTeacherDebitModal from 'modules/teacherDetails/components/EditTeacherDebitModal'
import { Tooltip } from '@mui/material'

const TruncatedComment = ({ comment }) => {
  const textRef = useRef(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth)
    }
  }, [comment])

  if (!isNotNilOrEmpty(comment)) {
    return '---'
  }

  return (
    <Tooltip 
      title={comment} 
      placement="top"
      disableHoverListener={!isOverflowing}
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: '14px',
            padding: '10px 14px',
            maxWidth: '400px'
          }
        }
      }}
    >
      <CommentText ref={textRef} $isOverflowing={isOverflowing}>
        {comment}
      </CommentText>
    </Tooltip>
  )
}

const TeacherDebitTable = () => {
  const list = useSelector(selectCurrentTeacherDebitItems)
  const sorted = reverse(sortBy(prop('date'), list))

  return (
    <>
      <StyledTable>
        <thead>
        <tr>
          <TypeCell>Co?</TypeCell>
          <StudentCell>Uczeń</StudentCell>
          <DateCell>Data</DateCell>
          <HoursCell>Liczba h</HoursCell>
          <RateCell>Stawka/h</RateCell>
          <ChangeCell>Zmiana salda</ChangeCell>
          <BalanceCell>Saldo po</BalanceCell>
          <CommentCell>Komentarz</CommentCell>
          <ActionsHeaderCell />
        </tr>
        </thead>
        <tbody>
        {
          sorted.map(item => (
            <TableRow value={item.value} key={item.id}>
              <td>{TEACHER_DEBIT_ITEM_TYPES[item.type]}</td>
              <td>{pathOr('---', ['student', 'name'], item)}</td>
              <td>{formatDate(item.date, DATE_FORMATS.dashed)}</td>
              <td>{propOr('---', 'duration', item)}</td>
              <td>{propOr('---', 'teacherRate', item)}</td>
              <td>{propOr('---', 'value', item)}</td>
              <td>{propOr('---', 'balanceAfter', item)}</td>
              <CommentTd>
                <TruncatedComment comment={item.comment} />
              </CommentTd>
              <td>
                <Actions onClick={e => e.stopPropagation()}>
                  {item.type !== 'lesson' && item.type !== 'expense' && (
                    <>
                      <EditSalaryModal salary={item} />
                      <DeleteSalaryModal salary={item} />
                    </>
                  )}
                  <EditTeacherDebitModal item={item} />
                </Actions>
              </td>
            </TableRow>
          ))
        }
        </tbody>
      </StyledTable>
      {isNilOrEmpty(list) && (
        <EmptyState>
          Brak wpisów do wyświetlenia
        </EmptyState>
      )}
    </>
  )
}

export default TeacherDebitTable

const StyledTable = styled.table`
  width: 100%;
  margin-top: 20px;
  font-size: 12px;
  position: relative;
  table-layout: fixed;
  
  th, td {
    text-align: left;
    padding: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  tr:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  
  th {
    border-bottom: 1px solid black;
    text-transform: uppercase;
  }
  
  tr {
    transition: all .3s;

    &:hover {
      background-color: ${({ theme }) => theme.colors.lightGrey};
    }
  }
`

const TypeCell = styled.th`
  width: 12%;
`

const StudentCell = styled.th`
  width: 18%;
`

const DateCell = styled.th`
  width: 10%;
`

const HoursCell = styled.th`
  width: 8%;
`

const RateCell = styled.th`
  width: 8%;
`

const ChangeCell = styled.th`
  width: 10%;
`

const BalanceCell = styled.th`
  width: 10%;
`

const CommentCell = styled.th`
  width: 16%;
`

const CommentTd = styled.td`
  overflow: hidden;
`

const CommentText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: ${({ $isOverflowing }) => $isOverflowing ? 'help' : 'default'};
`

const ActionsHeaderCell = styled.th`
  width: 8%;
`

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`

const TableRow = styled.tr`
  color: ${({ value }) => value === 0 ? 'default' : value > 0 ? 'green' : 'red'};
`

const EmptyState = styled.div`
  width: 100%;
  height: 70px;
  display: flex;
  align-items: center;
`
